import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { DIALOG_TYPE } from '../../../../constants';
import { AddCircleIcons, SearchIcons } from '../../../../icons/icons';
import { Dialog } from '../../../../ui';
import { FrameworkPagination, JobTitlesGrid } from '../components';
import { SORT_OPTIONS } from '../constants';
import { useJobTitleManagement } from '../hooks';
import { JobTitleManagementProps, SortOption } from '../interfaces';

export const JobTitleManagementContainer: React.FC<JobTitleManagementProps> = ({
  jobTitles = [],
}) => {
  const {
    newJobTitle,
    setNewJobTitle,
    searchQuery,
    setSearchQuery,
    isDeleting,
    deleteModalOpen,
    jobTitleToDelete,
    currentPage,
    sortBy,
    setSortBy,
    itemsPerPage,
    sortedAndFilteredJobTitles,
    totalPages,
    paginatedJobTitles,
    handleAddJobTitle,
    handleDeleteJobTitle,
    openDeleteModal,
    closeDeleteModal,
    handlePageChange,
  } = useJobTitleManagement({ jobTitles });

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header & Controls Section */}
      <Paper
        variant='outlined'
        sx={{
          borderRadius: '6px',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 2,
            pb: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            component='h2'
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: '-0.01em',
            }}
          >
            Job Titles
            <Typography
              component='span'
              sx={{
                ml: 1,
                fontSize: '0.875rem',
                fontWeight: 400,
                color: 'text.secondary',
              }}
            >
              ({sortedAndFilteredJobTitles.length.toLocaleString()})
            </Typography>
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          {/* Search Box */}
          <Box sx={{ flex: 1, minWidth: 220, maxWidth: { xs: '100%', md: 360 } }}>
            <TextField
              fullWidth
              size='small'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Search job titles...'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcons style={{ width: 16, height: 16, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: 'background.paper',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>

          {/* Sort Selection */}
          <Stack direction='row' alignItems='center' spacing={1} sx={{ flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
              Sort :
            </Typography>
            <Select
              size='small'
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: '6px',
                minWidth: 120,
                fontSize: '0.875rem',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                },
              }}
            >
              {SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.875rem' }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          {/* New Job Title Input */}
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <TextField
              fullWidth
              size='small'
              value={newJobTitle}
              onChange={(e) => setNewJobTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddJobTitle();
                }
              }}
              placeholder='Enter job title...'
              sx={{
                bgcolor: 'background.paper',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>

          {/* Add Button */}
          <Button
            variant='contained'
            onClick={handleAddJobTitle}
            disabled={!newJobTitle.trim()}
            startIcon={<AddCircleIcons style={{ width: 16, height: 16 }} />}
            sx={{
              borderRadius: '6px',
              px: 2.5,
              py: 0.9,
              bgcolor: '#093725',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none',
              flexShrink: 0,
              '&:hover': {
                bgcolor: '#062619',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              },
            }}
          >
            Add
          </Button>
        </Box>
      </Paper>

      {/* Grid & Pagination Section */}
      <Paper
        variant='outlined'
        sx={{
          borderRadius: '6px',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <JobTitlesGrid
          jobTitles={paginatedJobTitles}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          searchQuery={searchQuery}
          onDelete={openDeleteModal}
        />

        <FrameworkPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={sortedAndFilteredJobTitles.length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </Paper>

      <Dialog
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        handleApply={handleDeleteJobTitle}
        title='Delete Job Title'
        confirmButtonLable={isDeleting ? 'Deleting...' : 'Delete'}
        cancelButtonLable='Cancel'
        dialogType={DIALOG_TYPE.ERROR}
        disabled={isDeleting}
      >
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          Are you sure you want to delete{' '}
          <Box component='strong' sx={{ color: 'text.primary', fontWeight: 600 }}>
            &ldquo;{jobTitleToDelete?.label}&rdquo;
          </Box>
          ? This action cannot be undone.
        </Typography>
      </Dialog>
    </Box>
  );
};
